import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import SupplierService from "../../services/SupplierService";
import FinancialAccountService from "../../services/FinancialAccountService";
import ChequeService from "../../services/ChequeService";
import PromissoryNoteService from "../../services/PromissoryNoteService";
import expenseService from "../../services/ExpenseService";

const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 2,
});
const formatMoney = (val) => currencyFormatter.format(Number(val) || 0);

const paymentMethods = [
  { label: "Nakit (Kasa)", value: "Nakit", icon: "pi-money-bill", desc: "Kasa çıkışı" },
  { label: "Havale / EFT", value: "Havale/EFT", icon: "pi-building", desc: "Banka hesabı" },
  { label: "Kredi Kartı", value: "Kredi Kartı", icon: "pi-credit-card", desc: "Firma kartı" },
  { label: "Sanal POS", value: "Sanal POS", icon: "pi-globe", desc: "Online POS" },
  { label: "Firma Çeki", value: "Çek", icon: "pi-id-card", desc: "Çek keşide" },
  { label: "Firma Senedi", value: "Senet", icon: "pi-file", desc: "Senet düzenle" },
];

export default function QuickPaymentDialog({
  visible,
  onHide,
  payment,
  onSuccess,
}) {
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Nakit");
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    paymentDate: new Date(),
    financialAccountId: null,
    description: "",
    installmentCount: 1,
    firstInstallmentDate: new Date(),
  });

  // Cheque Details State
  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: "",
  });

  // Promissory Note Details State
  const [promissoryDetails, setPromissoryDetails] = useState({
    drawer: "",
    guarantor: "",
    dueDate: null,
  });

  const toast = useRef(null);

  useEffect(() => {
    if (visible && payment) {
      initDialog();
    }
  }, [visible, payment]);

  const loadFinancialAccounts = async () => {
    try {
      const res = await FinancialAccountService.getAll();
      const accounts = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || [];
      setFinancialAccounts(accounts);
      return accounts;
    } catch (err) {
      console.error("Hesaplar yüklenemedi", err);
      return [];
    }
  };

  const initDialog = async () => {
    let defaultMethod = "Nakit";
    if (payment?.sourceType === "CC_INSTALLMENT") {
      defaultMethod = "Havale/EFT";
    } else if (payment?.sourceType === "CHEQUE" && payment?.financialAccountId) {
      defaultMethod = "Havale/EFT";
    }
    setPaymentMethod(defaultMethod);

    let accounts = financialAccounts;
    if (!accounts || accounts.length === 0) {
      accounts = await loadFinancialAccounts();
    }

    let defaultAcc = null;
    if (accounts && accounts.length > 0) {
      if (payment?.financialAccountId) {
        defaultAcc = accounts.find((a) => a.id === payment.financialAccountId);
      }
      if (!defaultAcc) {
        defaultAcc = accounts.find((a) =>
          defaultMethod === "Nakit" ? a.accountType === 1 : a.accountType === 2
        ) || accounts[0];
      }
    }

    setPaymentForm({
      amount: payment?.remainingAmount || 0,
      paymentDate: new Date(),
      financialAccountId: defaultAcc ? defaultAcc.id : null,
      description: `${payment?.payeeName || ""} - ${payment?.documentNo ? payment?.documentNo + " nolu " : ""}Ödeme`,
      installmentCount: 1,
      firstInstallmentDate: new Date(),
    });

    setChequeDetails({
      bankName: "",
      bankBranch: "",
      checkNumber: "",
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      drawer: payment?.payeeName || "",
    });

    setPromissoryDetails({
      drawer: payment?.payeeName || "",
      guarantor: "",
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    });
  };

  // Filter accounts based on selected payment method
  const filteredAccounts = useMemo(() => {
    if (!financialAccounts || financialAccounts.length === 0) return [];
    if (paymentMethod === "Nakit") {
      const cashes = financialAccounts.filter((a) => a.accountType === 1);
      return cashes.length > 0 ? cashes : financialAccounts;
    }
    if (paymentMethod === "Havale/EFT" || paymentMethod === "Banka") {
      const banks = financialAccounts.filter((a) => a.accountType === 2);
      return banks.length > 0 ? banks : financialAccounts;
    }
    if (paymentMethod === "Kredi Kartı") {
      const cards = financialAccounts.filter((a) => a.accountType === 3 || a.accountType === 4);
      return cards.length > 0 ? cards : financialAccounts;
    }
    if (paymentMethod === "Sanal POS") {
      const pos = financialAccounts.filter((a) => a.accountType === 5);
      return pos.length > 0 ? pos : financialAccounts;
    }
    return financialAccounts;
  }, [financialAccounts, paymentMethod]);

  const accountOptions = useMemo(() => {
    return filteredAccounts.map((a) => {
      const typeLabel =
        a.accountType === 1
          ? "Kasa"
          : a.accountType === 2
          ? "Banka"
          : a.accountType === 3
          ? "Firma Kartı"
          : a.accountType === 4
          ? "POS"
          : a.accountType === 5
          ? "Sanal POS"
          : "Hesap";
      return {
        label: `${a.name} (${typeLabel}) - Bakiye: ${formatMoney(a.balance || 0)}`,
        value: a.id,
      };
    });
  }, [filteredAccounts]);

  // Auto-select first matching account when method changes
  useEffect(() => {
    if (filteredAccounts && filteredAccounts.length > 0) {
      const exists = filteredAccounts.some((a) => a.id === paymentForm.financialAccountId);
      if (!exists) {
        setPaymentForm((prev) => ({ ...prev, financialAccountId: filteredAccounts[0].id }));
      }
    }
  }, [paymentMethod, filteredAccounts]);

  const formatDateParam = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
  };

  const getSourceTypeBadge = (sourceType) => {
    switch (sourceType) {
      case "SUPPLIER_DUE":
      case "PURCHASE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800 whitespace-nowrap shrink-0">
            <i className="pi pi-truck text-xs" />
            <span>Tedarikçi Faturası</span>
          </span>
        );
      case "CC_INSTALLMENT":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200/80 dark:border-purple-800 whitespace-nowrap shrink-0">
            <i className="pi pi-credit-card text-xs" />
            <span>Kredi Kartı Taksiti</span>
          </span>
        );
      case "CHEQUE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-teal-50 dark:bg-teal-950/50 text-teal-700 dark:text-teal-300 border border-teal-200/80 dark:border-teal-800 whitespace-nowrap shrink-0">
            <i className="pi pi-id-card text-xs" />
            <span>Firma Çeki</span>
          </span>
        );
      case "PROMISSORY_NOTE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 border border-cyan-200/80 dark:border-cyan-800 whitespace-nowrap shrink-0">
            <i className="pi pi-file text-xs" />
            <span>Firma Senedi</span>
          </span>
        );
      case "EXPENSE":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800 whitespace-nowrap shrink-0">
            <i className="pi pi-wallet text-xs" />
            <span>İşletme Gideri</span>
          </span>
        );
      default:
        return (
          <Tag value={sourceType || "Ödeme"} severity="info" className="text-[10px] font-bold shrink-0" />
        );
    }
  };

  const handleSavePayment = async () => {
    if (!paymentForm.amount || paymentForm.amount <= 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Uyarı",
        detail: "Lütfen geçerli bir ödeme tutarı giriniz.",
      });
      return;
    }

    try {
      setSubmitting(true);
      const formattedDate = formatDateParam(paymentForm.paymentDate);

      if (payment.sourceType === "SUPPLIER_DUE" || payment.sourceType === "PURCHASE" || payment.supplierId) {
        await SupplierService.receivePayment({
          supplierId: payment.supplierId,
          amount: paymentForm.amount,
          paymentMethod: paymentMethod,
          paymentDate: formattedDate,
          description: paymentForm.description,
          financialAccountId: paymentForm.financialAccountId,
          supplierPayableDueItemId: payment.sourceType === "SUPPLIER_DUE" ? payment.sourceId : null,
          bankName: chequeDetails.bankName,
          bankBranch: chequeDetails.bankBranch,
          checkNumber: chequeDetails.checkNumber,
          dueDate: paymentMethod === "Çek" && chequeDetails.dueDate
            ? formatDateParam(chequeDetails.dueDate)
            : paymentMethod === "Senet" && promissoryDetails.dueDate
            ? formatDateParam(promissoryDetails.dueDate)
            : null,
          drawer: paymentMethod === "Çek"
            ? chequeDetails.drawer
            : paymentMethod === "Senet"
            ? promissoryDetails.drawer
            : null,
          guarantor: promissoryDetails.guarantor,
          installmentCount: paymentForm.installmentCount || 1,
          firstInstallmentDate: paymentForm.firstInstallmentDate ? formatDateParam(paymentForm.firstInstallmentDate) : null,
        });
      } else if (payment.sourceType === "CC_INSTALLMENT") {
        if (!paymentForm.financialAccountId) {
          toast.current?.show({
            severity: "warn",
            summary: "Uyarı",
            detail: "Lütfen ödemenin çıkacağı Kasa/Banka hesabını seçiniz.",
          });
          setSubmitting(false);
          return;
        }
        await FinancialAccountService.payInstallment({
          transactionId: payment.sourceId,
          sourceAccountId: paymentForm.financialAccountId,
        });
      } else if (payment.sourceType === "CHEQUE") {
        await ChequeService.updateStatus({
          chequeId: payment.sourceId,
          newStatus: 4, // Paid
          financialAccountId: paymentForm.financialAccountId,
          transactionDate: formattedDate,
          description: paymentForm.description || `Firma Çeki Ödemesi (${payment.payeeName})`,
        });
      } else if (payment.sourceType === "PROMISSORY_NOTE") {
        await PromissoryNoteService.updateStatus({
          promissoryNoteId: payment.sourceId,
          newStatus: 4, // Paid
          financialAccountId: paymentForm.financialAccountId,
          transactionDate: formattedDate,
          description: paymentForm.description || `Firma Senedi Ödemesi (${payment.payeeName})`,
        });
      } else if (payment.sourceType === "EXPENSE") {
        const res = await expenseService.getById(payment.sourceId);
        const expData = res?.data?.data || res?.data;
        if (expData) {
          await expenseService.update({
            ...expData,
            isPaid: true,
            financialAccountId: paymentForm.financialAccountId,
          });
        }
      }

      if (onSuccess) {
        onSuccess({
          payment,
          amount: paymentForm.amount,
          method: paymentMethod,
        });
      }

      onHide();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Ödeme işlemi gerçekleştirilirken bir hata oluştu.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!payment) return null;

  return (
    <>
      <Toast ref={toast} appendTo={typeof document !== "undefined" ? document.body : "self"} />
      <Dialog
        visible={visible}
        onHide={() => !submitting && onHide()}
        style={{ width: "95vw", maxWidth: "620px" }}
        modal
        showHeader={false}
        className="p-0 border-none shadow-2xl rounded-3xl overflow-hidden custom-payment-modal"
        content={({ hide }) => (
          <div className="flex flex-col bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200/80 dark:border-slate-800">
            {/* 1. Header Section - Fintech Dark Gradient */}
            <div className="bg-slate-900 text-white p-5 px-6 relative overflow-hidden shrink-0">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-20 -mt-20 blur-3xl pointer-events-none"></div>

              {/* Fixed Circular Close Button ("X") */}
              <button
                type="button"
                onClick={hide}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  width: "32px",
                  height: "32px",
                  minWidth: "32px",
                  maxWidth: "32px",
                  minHeight: "32px",
                  maxHeight: "32px",
                  borderRadius: "9999px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "rgba(255, 255, 255, 0.8)",
                  cursor: "pointer",
                  zIndex: 50,
                  flexShrink: 0,
                }}
                className="hover:!bg-white/25 hover:!text-white transition-all"
                disabled={submitting}
                title="Kapat"
              >
                <i className="pi pi-times text-xs" />
              </button>

              <div className="relative z-10 space-y-3">
                <div className="flex items-center gap-3 pr-10">
                  {/* Fixed Size Wallet Icon */}
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      minWidth: "42px",
                      maxWidth: "42px",
                      minHeight: "42px",
                      maxHeight: "42px",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "14px",
                      backgroundColor: "#10b981",
                      boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.3)",
                    }}
                  >
                    <i className="pi pi-wallet text-white text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block leading-tight">
                      HIZLI ÖDEME PLANI
                    </span>
                    <h2 className="text-base font-black text-white leading-tight truncate m-0 mt-0.5">
                      {payment.payeeName}
                    </h2>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-2.5 px-3">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                      İŞLEM TÜRÜ
                    </span>
                    <div className="text-xs font-bold text-slate-200 truncate flex items-center gap-1.5 flex-wrap">
                      {getSourceTypeBadge(payment.sourceType)}
                      {payment.financialAccountName && (
                        <span className="text-[10px] font-bold text-blue-300 bg-blue-900/60 px-1.5 py-0.5 rounded border border-blue-700/60">
                          {payment.financialAccountName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-2.5 px-3">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                      TOPLAM KALAN BORÇ
                    </span>
                    <div className="text-sm font-black text-rose-400">
                      {formatMoney(payment.remainingAmount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Scrollable Body Content */}
            <div className="p-5 px-6 space-y-4 overflow-y-auto max-h-[calc(85vh-200px)] custom-scrollbar bg-slate-50/40 dark:bg-slate-900">
              {/* Payment Method Selector Grid */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="pi pi-th-large text-emerald-500 text-xs" /> Ödeme Yöntemi Seçin{" "}
                  <span className="text-red-500">*</span>
                </label>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {paymentMethods.map((m) => {
                    const isSelected = paymentMethod === m.value;
                    return (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => setPaymentMethod(m.value)}
                        className={`group relative flex items-center gap-2.5 p-2.5 rounded-2xl border-2 transition-all duration-200 text-left cursor-pointer ${
                          isSelected
                            ? "border-emerald-500 bg-white dark:bg-slate-800 shadow-md shadow-emerald-500/10 ring-1 ring-emerald-500/30"
                            : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/80 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50/80"
                        }`}
                        style={{ minHeight: "52px" }}
                      >
                        <div
                          className={`rounded-xl flex items-center justify-center transition-all ${
                            isSelected
                              ? "bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 group-hover:bg-slate-200"
                          }`}
                          style={{
                            width: "32px",
                            height: "32px",
                            minWidth: "32px",
                            maxWidth: "32px",
                            minHeight: "32px",
                            maxHeight: "32px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i className={`pi ${m.icon} text-xs`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-xs font-black truncate leading-tight ${
                                isSelected
                                  ? "text-emerald-900 dark:text-emerald-200"
                                  : "text-slate-700 dark:text-slate-200"
                              }`}
                            >
                              {m.label}
                            </span>
                            {isSelected && (
                              <i className="pi pi-check text-emerald-500 text-[10px] font-bold shrink-0 ml-1" />
                            )}
                          </div>
                          <p className="text-[10px] text-slate-400 font-medium m-0 truncate mt-0.5 leading-tight">
                            {m.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic Form Subsections based on Method */}
              {/* 1. Çek Details Form */}
              {paymentMethod === "Çek" && (
                <div className="bg-teal-50/60 dark:bg-teal-950/20 p-4 rounded-2xl border border-teal-200/80 dark:border-teal-900/40 space-y-3 animate-fade-in">
                  <div className="text-[11px] font-black uppercase text-teal-800 dark:text-teal-300 flex items-center gap-1.5">
                    <i className="pi pi-id-card text-xs" /> ÇEK BİLGİLERİ
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Banka Adı</label>
                      <InputText
                        value={chequeDetails.bankName}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, bankName: e.target.value })
                        }
                        placeholder="Örn: Garanti BBVA"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Şube</label>
                      <InputText
                        value={chequeDetails.bankBranch}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, bankBranch: e.target.value })
                        }
                        placeholder="Örn: Kadıköy Şb."
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Çek No</label>
                      <InputText
                        value={chequeDetails.checkNumber}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, checkNumber: e.target.value })
                        }
                        placeholder="Örn: 004821"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Vade Tarihi</label>
                      <Calendar
                        value={chequeDetails.dueDate}
                        onChange={(e) =>
                          setChequeDetails({ ...chequeDetails, dueDate: e.value })
                        }
                        showIcon
                        dateFormat="dd.mm.yy"
                        appendTo={typeof document !== "undefined" ? document.body : "self"}
                        className="w-full custom-emerald-calendar"
                        buttonClassName="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white !rounded-r-xl !px-3"
                        inputClassName="py-2 rounded-l-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                        panelClassName="z-[50000] bg-white dark:bg-slate-900 shadow-2xl rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 2. Senet Details Form */}
              {paymentMethod === "Senet" && (
                <div className="bg-cyan-50/60 dark:bg-cyan-950/20 p-4 rounded-2xl border border-cyan-200/80 dark:border-cyan-900/40 space-y-3 animate-fade-in">
                  <div className="text-[11px] font-black uppercase text-cyan-800 dark:text-cyan-300 flex items-center gap-1.5">
                    <i className="pi pi-file text-xs" /> SENET BİLGİLERİ
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">
                        Borçlu / Keşideci
                      </label>
                      <InputText
                        value={promissoryDetails.drawer}
                        onChange={(e) =>
                          setPromissoryDetails({ ...promissoryDetails, drawer: e.target.value })
                        }
                        placeholder="Firma / Şahıs Adı"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">Kefil</label>
                      <InputText
                        value={promissoryDetails.guarantor}
                        onChange={(e) =>
                          setPromissoryDetails({
                            ...promissoryDetails,
                            guarantor: e.target.value,
                          })
                        }
                        placeholder="Varsa kefil adı"
                        className="w-full py-2 rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500">
                        Senet Vade Tarihi
                      </label>
                      <Calendar
                        value={promissoryDetails.dueDate}
                        onChange={(e) =>
                          setPromissoryDetails({ ...promissoryDetails, dueDate: e.value })
                        }
                        showIcon
                        dateFormat="dd.mm.yy"
                        appendTo={typeof document !== "undefined" ? document.body : "self"}
                        className="w-full custom-emerald-calendar"
                        buttonClassName="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white !rounded-r-xl !px-3"
                        inputClassName="py-2 rounded-l-xl text-xs font-bold border-slate-200 dark:border-slate-700"
                        panelClassName="z-[50000] bg-white dark:bg-slate-900 shadow-2xl rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Core Fields */}
              <div className="space-y-3 pt-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Payment Amount */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span>Ödenecek Tutar (TL)</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <InputNumber
                      value={paymentForm.amount}
                      onValueChange={(e) =>
                        setPaymentForm({ ...paymentForm, amount: e.value })
                      }
                      mode="currency"
                      currency="TRY"
                      locale="tr-TR"
                      min={0.01}
                      max={payment.remainingAmount}
                      className="w-full"
                      inputClassName="rounded-xl border-slate-200 dark:border-slate-700 py-2.5 font-black text-sm text-slate-800 dark:text-white bg-white dark:bg-slate-800"
                    />
                  </div>

                  {/* Payment Date */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                      <span>İşlem Tarihi</span>
                      <span className="text-red-500">*</span>
                    </label>
                    <Calendar
                      value={paymentForm.paymentDate}
                      onChange={(e) =>
                        setPaymentForm({ ...paymentForm, paymentDate: e.value })
                      }
                      showIcon
                      dateFormat="dd.mm.yy"
                      appendTo={typeof document !== "undefined" ? document.body : "self"}
                      className="w-full custom-emerald-calendar"
                      buttonClassName="!bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white !rounded-r-xl !px-3"
                      inputClassName="rounded-l-xl border-slate-200 dark:border-slate-700 py-2.5 font-bold text-xs bg-white dark:bg-slate-800"
                      panelClassName="z-[50000] bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 rounded-xl"
                    />
                  </div>
                </div>

                {/* Financial Account (Kasa / Banka / Kart / POS) */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    {paymentMethod === "Nakit"
                      ? "Ödemenin Çıkacağı Kasa Hesabı"
                      : paymentMethod === "Havale/EFT" || paymentMethod === "Banka"
                      ? "Ödemenin Çıkacağı Banka Hesabı"
                      : paymentMethod === "Kredi Kartı"
                      ? "Ödemenin Yapıldığı Firma Kartı"
                      : paymentMethod === "Sanal POS"
                      ? "İşlem Yapılacak Sanal POS Hesabı"
                      : "İlişkili Hesap (Opsiyonel)"}
                  </label>
                  <Dropdown
                    value={paymentForm.financialAccountId}
                    options={accountOptions}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, financialAccountId: e.value })
                    }
                    placeholder="Hesap Seçiniz..."
                    appendTo={typeof document !== "undefined" ? document.body : "self"}
                    className="w-full rounded-xl border-slate-200 dark:border-slate-700 text-xs font-bold bg-white dark:bg-slate-800"
                    panelClassName="custom-dropdown-panel shadow-2xl border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900"
                    emptyMessage="Bu ödeme yöntemine uygun aktif hesap bulunamadı."
                  />
                </div>

                {/* Description */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Açıklama
                  </label>
                  <InputText
                    value={paymentForm.description}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, description: e.target.value })
                    }
                    placeholder="Ödeme açıklaması giriniz..."
                    className="w-full rounded-xl border-slate-200 dark:border-slate-700 py-2.5 text-xs font-bold bg-white dark:bg-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* 3. Solid Fixed Footer */}
            <div className="p-4 px-6 bg-white dark:bg-slate-900 border-t border-slate-200/80 dark:border-slate-800 flex items-center justify-between shrink-0">
              <div className="text-xs text-slate-400 font-medium">
                Kalan Borç:{" "}
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {formatMoney(payment.remainingAmount)}
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <Button
                  label="Vazgeç"
                  icon="pi pi-times"
                  className="p-button-text p-button-secondary !rounded-xl !text-xs !font-bold !py-2.5 !px-4 hover:!bg-slate-100 dark:hover:!bg-slate-800"
                  onClick={hide}
                  disabled={submitting}
                />
                <Button
                  label="Ödemeyi Tamamla"
                  icon="pi pi-check"
                  className="!bg-gradient-to-r !from-emerald-600 !to-teal-600 hover:!from-emerald-700 hover:!to-teal-700 !border-none !rounded-xl !text-xs !font-black !px-6 !py-2.5 text-white shadow-lg shadow-emerald-600/25 transition-all"
                  onClick={handleSavePayment}
                  loading={submitting}
                />
              </div>
            </div>
          </div>
        )}
      />
    </>
  );
}
